import { useRef } from "react";

import { formatTypeMismatch } from "../engine/computed-type-check.js";
import { logTypeMismatch } from "../engine/type-mismatch-reporting.js";

/**
 * Owns the report/resolve bookkeeping for Computation type mismatches so
 * useFunctionRecompute holds only recompute lifecycle. See
 * computed-type-check.js for what a mismatch is and why it must not be stored.
 *
 * @param {Function | undefined} onTypeMismatch the caller's UI callback; see
 *   useFunctionRecompute's param doc for the message shapes.
 */
export function useTypeMismatchReporting(onTypeMismatch) {
    // Keep the callback in a ref so the recompute effects don't have to list it
    // as a dependency — a caller passing an inline arrow (the common case)
    // would otherwise re-arm the initial-compute guard on every render.
    const onTypeMismatchRef = useRef(onTypeMismatch);
    onTypeMismatchRef.current = onTypeMismatch;

    // Telemetry / console are reported once per (oid + expression) for the life
    // of the form. The reactive pass re-evaluates on every keystroke, and a
    // broken expression stays broken, so without this one bad function would
    // emit a warning per debounce tick. The UI callback is NOT deduped here —
    // the preview panel keys mismatches by oid itself, and needs the repeat so
    // it can re-show a mismatch after a new form definition is loaded.
    const reportedMismatches = useRef(new Set());
    // OIDs currently in mismatch. Separate from the dedupe set above because
    // resolution is keyed by item, not by (oid + expression): the same
    // expression can start and stop producing the wrong TYPE as data changes,
    // e.g. `SMOKER === "1" ? CIGS * 20 : "N/A"` on a Number item. Without this,
    // resolution would either be signalled for every clean item on every pass,
    // or never at all.
    const mismatchedOids = useRef(new Set());

    /**
     * Hands a type mismatch to the caller's UI (if it wants one) and, once per
     * offending expression, to the console and to AppInsights.
     *
     * The UI callback is how the Designer preview surfaces this; the live
     * patient flow passes none, because a patient must never be shown
     * designer-facing text. Telemetry runs in both so a mismatch that only
     * shows up in production is still discoverable.
     */
    const reportTypeMismatch = (mismatch) => {
        const message = formatTypeMismatch(mismatch);

        mismatchedOids.current.add(mismatch.oid);
        onTypeMismatchRef.current?.({ ...mismatch, message });

        const key = `${mismatch.oid}|${mismatch.expression}`;
        if (reportedMismatches.current.has(key)) return;
        reportedMismatches.current.add(key);

        logTypeMismatch(mismatch, message);
    };

    /**
     * Signals that an item previously reported as a mismatch now produces an
     * assignable value, so the caller's UI can drop its row.
     *
     * Without this the preview panel keeps saying "The value was cleared" while
     * the field right below it shows a perfectly good value — and a panel that
     * contradicts the form is worse than no panel, because the designer learns
     * to ignore it. Only fires for OIDs actually reported, so a clean form
     * stays silent. See PR !40046 thread 290105.
     */
    const resolveTypeMismatch = (oid) => {
        if (!mismatchedOids.current.delete(oid)) return;

        // Drop this item's telemetry dedupe entries too. They are keyed by
        // (oid + expression) and suppress repeats for the life of the form, so
        // leaving them behind would mean a mismatch that resolves and then
        // recurs in the same session is never reported a second time.
        for (const key of reportedMismatches.current) {
            if (key.startsWith(`${oid}|`)) {
                reportedMismatches.current.delete(key);
            }
        }

        onTypeMismatchRef.current?.({ oid, resolved: true });
    };

    /**
     * Forgets everything — for a definition swap (SET_FORM). A new definition
     * means new expressions: their mismatches must be logged again rather than
     * suppressed by the previous form's keys, and an OID carried over from the
     * previous definition must not suppress the new one's first report.
     */
    const resetTypeMismatches = () => {
        reportedMismatches.current.clear();
        mismatchedOids.current.clear();
    };

    return { reportTypeMismatch, resolveTypeMismatch, resetTypeMismatches };
}
